import { createElement } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useSetContextualNav } from '@/components/layout/nav-context';
import {
  useServiceDetail,
  ServiceAccessError,
  liveCDNConfig,
  liveFileTransferConfig,
  liveVerimatrixConfig,
} from '@/api/media';
import { useServiceAccess } from '@/api/service-access';
import { LiveConsoleBreadcrumbs } from './LiveConsoleBreadcrumbs';
import { LIVE_NAV, liveHref, liveActiveId } from './live-console-nav';
import { LiveDashboard } from './dashboard/DashboardPage';
import { LiveAnalysis } from './analysis/AnalysisPage';
import { LiveCurrentJobs } from './current-jobs/CurrentJobsList';
import { LiveCurrentJobDetail } from './current-jobs/CurrentJobDetail';
import { LivePresets } from './transcoding-presets/PresetsList';
import { LivePresetCreate } from './transcoding-presets/PresetCreate';
import { LivePresetSelect } from './transcoding-presets/PresetSelect';
import { LiveEventJobCreate } from './event-jobs/event-job-create/EventJobCreate';
import { LiveLinearJobCreate } from './linear-jobs/linear-job-create/LinearJobCreate';
import { LiveSettingsTemplates } from './settings-templates/SettingsTemplatesList';
import { LiveProvisioning } from './provisioning/ProvisioningPage';
import { LiveAudit } from './audit/AuditList';
import { LiveNotifications } from './notifications/NotificationsList';
import { FileTransferList } from '../shared/FileTransferList';
import { CdnEndpointsList } from '../shared/CdnEndpointsList';
import { VerimatrixDrmList } from '../shared/VerimatrixDrmList';

// Slugs a report-only member may reach. The BE enforces the same set; the FE
// gate hides management entries from the nav and redirects management routes.
const REPORT_SLUGS = new Set(['', 'analysis', 'audit']);

/** Redirects the legacy `…/:streamId/monitor` deep-link to the unified detail
 *  page with the Live Monitor tab pre-selected. */
function MonitorRedirect() {
  const { serviceId = '', streamId = '' } = useParams<{ serviceId: string; streamId: string }>();
  return <Navigate to={`/services/live/${serviceId}/current-jobs/${streamId}?tab=monitor`} replace />;
}

/**
 * Live Transcoding console — live event/linear transcoding with presets,
 * current-job monitoring, recording, analysis and delivery settings
 * (storage / CDN endpoints / DRM / domain templates), per provisioned
 * live-stream service. Sub-pages register in the app's ContextualSidebar,
 * same scope-swap model as the other service consoles.
 */
export function LiveConsole() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { serviceId = '' } = useParams<{ serviceId: string }>();
  const service = useServiceDetail(serviceId);
  const base = `/services/live/${serviceId}`;
  const hrefFor = (slug: string) => liveHref(base, slug);

  // Report-only members see read-only sections (Dashboard / Analysis / Audit).
  // The BE 403s management routes regardless — this hides them in the UI.
  const { data: accessMode } = useServiceAccess(serviceId);
  const report = accessMode === 'report';

  useSetContextualNav(() => {
    const active = liveActiveId(base, pathname);
    // For report members: keep only groups that have at least one read-only entry.
    const groups = report
      ? LIVE_NAV.map((group) => ({
          ...group,
          entries: group.entries.filter((e) => REPORT_SLUGS.has(e.slug)),
        })).filter((group) => group.entries.length > 0)
      : LIVE_NAV;
    return {
      sections: groups.map((group) => ({
        heading: group.heading,
        items: group.entries.map((e) => ({
          id: hrefFor(e.slug),
          label: e.label,
          icon: createElement(e.icon, { size: 16 }),
          disabled: e.disabled,
          badge: e.badge,
          onSelect: e.disabled ? undefined : () => navigate(hrefFor(e.slug)),
        })),
      })),
      activeId: active,
      back: { label: 'Back to Services', onSelect: () => navigate('/services') },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, pathname, navigate, report]);

  // No access / no such service (hard HTTP or soft 200-body status) — bounce to
  // the services dashboard (legacy behavior).
  const errStatus =
    service.error instanceof ServiceAccessError
      ? service.error.status
      : isAxiosError(service.error)
        ? service.error.response?.status
        : undefined;
  if (errStatus === 403 || errStatus === 404) return <Navigate to="/services" replace />;

  // Redirects report members away from management routes to the console base.
  const managed = (el: JSX.Element) => (report ? <Navigate to={base} replace /> : el);

  // Thin shell: nav registration + error redirect + routes only. Each sub-page
  // supplies its own page chrome (Container + PageHeader + breadcrumb); the
  // service identity/status now lives on the dashboard page.
  return (
    <Routes>
      {/* Index and /dashboard both render Dashboard; deep-links still work. */}
      <Route index element={<LiveDashboard serviceId={serviceId} />} />
      <Route path="dashboard" element={<LiveDashboard serviceId={serviceId} />} />
      <Route path="current-jobs" element={managed(<LiveCurrentJobs serviceId={serviceId} />)} />
      <Route path="current-jobs/:streamId" element={managed(<LiveCurrentJobDetail serviceId={serviceId} />)} />
      {/* Legacy monitor deep-link → unified detail page, Live Monitor tab. */}
      <Route path="current-jobs/:streamId/monitor" element={managed(<MonitorRedirect />)} />
      <Route path="transcoding-presets" element={managed(<LivePresets serviceId={serviceId} />)} />
      <Route path="transcoding-presets/create" element={managed(<LivePresetCreate serviceId={serviceId} />)} />
      <Route path="transcoding-presets/select" element={managed(<LivePresetSelect serviceId={serviceId} />)} />
      <Route path="event-jobs/create" element={managed(<LiveEventJobCreate serviceId={serviceId} />)} />
      <Route path="linear-jobs/create" element={managed(<LiveLinearJobCreate serviceId={serviceId} />)} />
      <Route path="analysis" element={<LiveAnalysis serviceId={serviceId} />} />
      <Route path="audit" element={<LiveAudit serviceId={serviceId} />} />
      <Route path="notifications" element={managed(<LiveNotifications serviceId={serviceId} />)} />
      <Route path="domain-templates" element={managed(<LiveSettingsTemplates serviceId={serviceId} />)} />
      <Route path="file-transfer" element={managed(<FileTransferList serviceId={serviceId} config={liveFileTransferConfig} breadcrumbs={<LiveConsoleBreadcrumbs trail={[{ label: 'File Transfer' }]} />} />)} />
      <Route path="cdn-endpoints" element={managed(<CdnEndpointsList serviceId={serviceId} config={liveCDNConfig} breadcrumbs={<LiveConsoleBreadcrumbs trail={[{ label: 'CDN Endpoints' }]} />} />)} />
      <Route path="verimatrix-drms" element={managed(<VerimatrixDrmList serviceId={serviceId} config={liveVerimatrixConfig} breadcrumbs={<LiveConsoleBreadcrumbs trail={[{ label: 'Verimatrix DRMs' }]} />} />)} />
      <Route path="provisioning" element={managed(<LiveProvisioning serviceId={serviceId} />)} />
      {/* Unmatched paths fall through to Dashboard (no redirect loop). */}
      <Route path="*" element={<LiveDashboard serviceId={serviceId} />} />
    </Routes>
  );
}

export default LiveConsole;
